// admin/lancar-enviar.js — modal "Revisar antes de salvar" + POST ao backend (Fase 7).
//
// Por que módulo separado:
//   lancar.js passa de 400 linhas se absorver essa lógica. Precedente:
//   oratoria.js + oratoria-render.js.
//
// Responsabilidade única: dado o rascunho + lista de alunos + identidade do
// admin, abre um modal com preview da gravação (quem vai receber qual
// status), coleta motivos opcionais para faltas/justificadas/atrasos, envia
// via api.post('registrar', ...) e devolve o resultado para o caller.
//
// Dependências:
//   - api    (core/api.js)    — api.post('registrar', body)
//   - notify (core/notify.js) — toasts de sucesso/erro
//
// Exporta:
//   abrirModalEnviar({ container, admin, rascunho, alunosTurma, onSucesso })

import { api }    from '../core/api.js';
import { notify } from '../core/notify.js';

const LABELS = {
  presente:    { emoji: '✅', nome: 'Presente',    cor: '#34c759' },
  falta:       { emoji: '❌', nome: 'Falta',       cor: '#ff3b30' },
  justificado: { emoji: '📋', nome: 'Justificado', cor: '#ff9500' },
  atrasado:    { emoji: '⏰', nome: 'Atrasado',    cor: '#007AFF' },
};

// Status que se beneficiam de um campo "motivo" no modal
const PEDE_MOTIVO = new Set(['falta', 'justificado', 'atrasado']);

/**
 * Define o turno (manha/tarde/noite) a partir da data+hora atual ou da hora
 * escolhida pelo admin. Por ora, deriva da hora atual — Fase 8 pode evoluir
 * para um seletor explícito.
 */
function inferirTurno() {
  const h = new Date().getHours();
  if (h < 12) return 'manha';
  if (h < 18) return 'tarde';
  return 'noite';
}

/**
 * Escapa HTML básico para usar em template literais sem XSS.
 */
function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Abre o modal de revisão.
 *
 * @param {object} args
 * @param {HTMLElement} args.container
 * @param {object}      args.admin        { cpf, nome } do admin logado (Fase 8)
 * @param {object}      args.rascunho     { data, turma, registros: {cpf:status} }
 * @param {Array}       args.alunosTurma  lista de alunos da turma selecionada
 * @param {Function}    args.onSucesso    chamado após POST ok — caller limpa rascunho
 */
export function abrirModalEnviar({ container, admin, rascunho, alunosTurma, onSucesso }) {
  // Monta lista de registros a enviar (só alunos com status marcado)
  const registros = alunosTurma
    .filter(a => rascunho.registros[a.senha])
    .map(a => ({
      cpf:    a.senha,
      nome:   a.nome,
      status: rascunho.registros[a.senha],
      motivo: '',
    }));

  if (!registros.length) {
    notify.error('Nenhum status lançado ainda — marque pelo menos um aluno.');
    return;
  }

  const turno = inferirTurno();

  // Constrói o overlay
  const overlay = document.createElement('div');
  overlay.id = 'lancarEnviarOverlay';
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10000;
    display:flex;align-items:center;justify-content:center;padding:20px;
    font-family:'Montserrat',sans-serif;`;

  overlay.innerHTML = `
    <div style="background:#fff;border-radius:18px;max-width:520px;width:100%;max-height:88vh;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 16px 48px rgba(0,0,0,0.3)">
      <div style="padding:20px 22px;border-bottom:1px solid #F0F0F0">
        <div style="font-size:10px;font-weight:800;color:#6A001F;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px">Confirmar envio</div>
        <div style="font-size:18px;font-weight:800;color:#0A0A0A">Revisar antes de salvar</div>
        <div style="font-size:12px;color:#9B9B9B;margin-top:6px">
          Turma <strong>${esc(rascunho.turma)}</strong> · ${esc(rascunho.data)} · turno <strong>${esc(turno)}</strong>
          · <strong>${registros.length}</strong> lançamento(s)
        </div>
      </div>

      <div id="lancarEnviarLista" style="flex:1;overflow-y:auto;padding:12px 22px">
        ${registros.map((r, i) => {
          const L = LABELS[r.status] || { emoji: '•', nome: r.status, cor: '#666' };
          const precisaMotivo = PEDE_MOTIVO.has(r.status);
          return `
            <div style="padding:12px 0;border-bottom:1px solid #F5F5F5">
              <div style="display:flex;align-items:center;gap:10px">
                <div style="flex:1;min-width:0">
                  <div style="font-size:13px;font-weight:700;color:#0A0A0A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(r.nome)}</div>
                  <div style="font-size:11px;color:#9B9B9B">CPF ${esc(r.cpf)}</div>
                </div>
                <span style="font-size:11px;font-weight:800;color:${L.cor};background:${L.cor}22;padding:4px 10px;border-radius:100px;white-space:nowrap">
                  ${L.emoji} ${L.nome}
                </span>
              </div>
              ${precisaMotivo ? `
                <input data-motivo-idx="${i}" type="text" maxlength="120"
                  placeholder="Motivo (opcional)"
                  style="width:100%;margin-top:8px;padding:8px 10px;border-radius:8px;border:1px solid #E8E8E8;font-family:'Montserrat',sans-serif;font-size:12px;color:#0A0A0A;box-sizing:border-box">
              ` : ''}
            </div>`;
        }).join('')}
      </div>

      <div id="lancarEnviarFooter" style="padding:16px 22px;border-top:1px solid #F0F0F0;display:flex;gap:10px">
        <button id="lancarEnviarCancelar" style="
          flex:1;padding:12px;border-radius:10px;border:1.5px solid #E8E8E8;background:#fff;
          font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;color:#6B6B6B;cursor:pointer
        ">Cancelar</button>
        <button id="lancarEnviarConfirmar" style="
          flex:2;padding:12px;border-radius:10px;border:none;
          background:linear-gradient(90deg,#6A001F,#B3002D);color:#fff;
          font-family:'Montserrat',sans-serif;font-size:13px;font-weight:800;cursor:pointer
        ">Enviar ${registros.length} lançamento(s)</button>
      </div>
    </div>`;

  container.appendChild(overlay);

  const fechar = () => overlay.remove();
  overlay.addEventListener('click', e => { if (e.target === overlay) fechar(); });
  overlay.querySelector('#lancarEnviarCancelar').addEventListener('click', fechar);

  const btnConfirmar = overlay.querySelector('#lancarEnviarConfirmar');
  btnConfirmar.addEventListener('click', async () => {
    // Coleta motivos preenchidos
    overlay.querySelectorAll('input[data-motivo-idx]').forEach(inp => {
      const i = Number(inp.dataset.motivoIdx);
      registros[i].motivo = inp.value.trim();
    });

    // Estado de loading
    btnConfirmar.disabled = true;
    btnConfirmar.textContent = 'Enviando...';
    btnConfirmar.style.opacity = '0.7';

    const body = {
      admin,
      aula: { data: rascunho.data, turno, turma: String(rascunho.turma) },
      registros,
    };

    try {
      const res = await api.post('registrar', body);
      notify.success(`${res?.gravados || registros.length} lançamento(s) enviado(s) ao servidor`);
      fechar();
      if (typeof onSucesso === 'function') onSucesso(res);
    } catch (err) {
      console.error('[lancar-enviar] falha no POST:', err);
      notify.error(err?.message || 'Falha no envio ao servidor. Rascunho preservado.');
      btnConfirmar.disabled = false;
      btnConfirmar.textContent = `Tentar novamente`;
      btnConfirmar.style.opacity = '1';
    }
  });
}
